/**
 * 精读页的题目区 HTML 生成。
 *
 * 为什么不交给 React 渲染：精读页要求题干、选项都能被选中并高亮，
 * 而高亮是通过**包裹/切分 DOM 节点**实现的（lib/annotate.ts）。只要 React 还托管着这些节点，
 * 一次重渲染就会把所有包裹抹掉。所以这一块和文章一样，由本模块生成 HTML、
 * 再交给 decoratePassage + applyAnnotations 处理。
 *
 * 锚点约定（与服务端的 assertAnchor 一一对应）：
 *   · 题干            data-block-id="{questionId}"        例如 q14
 *   · 题目自带的选项  data-block-id="{questionId}#{key}"  例如 q14#A
 *   · 题组共用的选项  data-block-id="{groupId}#{key}"     例如 g2#B
 * 三者都带 data-anchor-kind="question"。
 */
import { FIXED_OPTIONS, KIND_LABELS, optionAnchorId, optionsForQuestion, } from '@leits/shared/client';
export function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/** 把内容 schema 里的 `<strong>N</strong> ______` 空位换成可见的填空下划线。 */
function promptHtml(question) {
    return question.promptHtml.replace(/_{2,}/g, '<span class="blank-slot">____</span>');
}
function optionRowHtml(anchorId, option, correct) {
    return [
        `<div class="cr-option${correct ? ' is-correct' : ''}" data-option-key="${escapeHtml(option.key)}">`,
        `<span class="option__key">${escapeHtml(option.key)}</span>`,
        `<span class="cr-option__text" data-anchor-kind="question" data-block-id="${escapeHtml(anchorId)}">${escapeHtml(option.text)}</span>`,
        correct ? '<span class="cr-option__mark">正确答案</span>' : '',
        '</div>',
    ].join('');
}
/** 正确答案的人类可读写法：选项 key 顺带把选项文字带上，方便回看。 */
function answerText(question, options) {
    return question.answer.accepted
        .map((accepted) => {
        const option = options.find((item) => item.key === accepted);
        if (!option)
            return escapeHtml(accepted);
        if (option.text === option.key)
            return escapeHtml(accepted);
        return `${escapeHtml(accepted)} — ${escapeHtml(option.text)}`;
    })
        .join('<span class="cr-answer__sep"> / </span>');
}
/**
 * 判断这一组的选项是「每题各渲染一份」还是「整组共用一份」。
 *
 * 单选/多选是每题一组选项（题干变了选项也变），所以渲染在题下；
 * 标题匹配、判断题这类是整组共用一套选项，渲染在题组抬头处一份就够了——
 * 否则 8 个标题会在 7 道题下面各重复一遍。
 */
function planGroup(group, allQuestions) {
    const questions = group.questionIds
        .map((id) => allQuestions.find((question) => question.id === id))
        .filter((question) => question !== undefined);
    const first = questions[0];
    const shared = first && !first.options && (FIXED_OPTIONS[first.kind] || group.options) ? true : false;
    let sharedOptions = null;
    if (shared && first) {
        sharedOptions = optionsForQuestion(first, group);
    }
    return { group, questions, sharedOptions };
}
/** 生成整个题目区的 HTML。 */
export function buildQuestionsHtml(exam) {
    const plans = exam.questionGroups.map((group) => planGroup(group, exam.questions));
    return plans
        .map(({ group, questions, sharedOptions }) => {
        const bounds = questions.length > 0
            ? `第 ${questions[0]?.displayNumber}–${questions[questions.length - 1]?.displayNumber} 题`
            : '';
        const sharedHtml = sharedOptions
            ? [
                '<div class="cr-shared-options">',
                '<span class="cr-shared-options__label">选项</span>',
                sharedOptions
                    .map((option) => optionRowHtml(`${group.id}#${option.key}`, option, questions.some((question) => question.answer.accepted.includes(option.key))))
                    .join(''),
                '</div>',
            ].join('')
            : '';
        const questionsHtml = questions
            .map((question) => {
            const ownOptions = question.options ? optionsForQuestion(question, group) : [];
            const optionsHtml = ownOptions.length > 0
                ? `<div class="cr-options">${ownOptions
                    .map((option) => optionRowHtml(optionAnchorId(question.id, option.key), option, question.answer.accepted.includes(option.key)))
                    .join('')}</div>`
                : '';
            const locate = question.explanation?.locate;
            const explanationHtml = locate
                ? `<div class="cr-explain">定位：${locate.paragraphId ? `段落 ${escapeHtml(locate.paragraphId)}` : ''}${locate.sentenceIndex !== undefined ? ` · 第 ${locate.sentenceIndex + 1} 句` : ''}${locate.quote ? ` · ${escapeHtml(locate.quote)}` : ''}${question.explanation?.note ? ` · ${escapeHtml(question.explanation.note)}` : ''}</div>`
                : '';
            return [
                `<article class="cr-question" id="cr-question-${escapeHtml(question.id)}" data-question-id="${escapeHtml(question.id)}">`,
                '<div class="cr-question__head">',
                `<span class="question__number">${question.displayNumber}</span>`,
                `<div class="cr-prompt" data-anchor-kind="question" data-block-id="${escapeHtml(question.id)}">${promptHtml(question)}</div>`,
                '</div>',
                optionsHtml,
                `<div class="cr-answer">正确答案：<strong>${answerText(question, optionsForQuestion(question, group))}</strong></div>`,
                explanationHtml,
                '</article>',
            ].join('');
        })
            .join('');
        return [
            `<section class="cr-group" data-group-id="${escapeHtml(group.id)}">`,
            '<div class="cr-group__head">',
            `<span class="badge badge--accent">${escapeHtml(KIND_LABELS[group.kind])}</span>`,
            `<span class="faint" style="font-size:12px">${bounds}</span>`,
            '</div>',
            group.instructionsHtml
                ? `<div class="cr-group__instructions">${group.instructionsHtml}</div>`
                : '',
            sharedHtml,
            '<div class="cr-questions">',
            questionsHtml,
            '</div>',
            '</section>',
        ].join('');
    })
        .join('');
}
//# sourceMappingURL=closeReading.js.map